import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import {
  LogOut, Upload, Trash2, Loader2, Image as ImageIcon, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { BRAND, PRODUCTS } from '../data/mock';
import AdminTabs from '../components/AdminTabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const absoluteUrl = (u) => (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const ProductPanel = ({ product, images, onUpload, onDelete, uploading }) => {
  const inputRef = useRef(null);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) await onUpload(product.slug, f);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <section
      className="bg-white rounded-3xl border border-pink-100 shadow-soft p-6"
      data-testid={`admin-product-panel-${product.slug}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-blush flex-shrink-0">
          <img src={product.hero} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-stone-900">{product.name}</h3>
          <p className="text-sm text-stone-500 italic line-clamp-1">{product.tagline}</p>
          <p className="text-xs text-stone-400 mt-1">{images.length} photo{images.length === 1 ? '' : 's'} uploaded</p>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
            data-testid={`admin-upload-input-${product.slug}`}
          />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white font-semibold shadow-pink-glow disabled:opacity-70"
            data-testid={`admin-upload-button-${product.slug}`}
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading…' : 'Upload photos'}
          </Button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-pink-200 bg-blush/30 p-8 text-center text-sm text-stone-500" data-testid={`admin-empty-${product.slug}`}>
          <ImageIcon className="w-6 h-6 text-pink-brand mx-auto mb-2" />
          No real photos yet. The public product page falls back to placeholder images until you upload.
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid={`admin-gallery-${product.slug}`}>
          {images.map((img) => (
            <div
              key={img.id}
              className="group relative aspect-square rounded-xl overflow-hidden bg-blush border border-pink-100"
              data-testid={`admin-image-${img.id}`}
            >
              <img src={absoluteUrl(img.url)} alt={img.original_filename} className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => onDelete(product.slug, img.id)}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete"
                data-testid={`admin-delete-image-${img.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <Link
          to={`/products/${product.slug}`}
          target="_blank"
          className="text-xs text-pink-brand font-semibold hover:underline"
          data-testid={`admin-view-live-${product.slug}`}
        >
          View public page ↗
        </Link>
      </div>
    </section>
  );
};

const AdminProductImages = () => {
  const { user, loading, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [imagesBySlug, setImagesBySlug] = useState({});
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    const entries = await Promise.all(
      PRODUCTS.map(async (p) => {
        try {
          const { data } = await authAxios.get(`/products/${p.slug}/images`);
          return [p.slug, data];
        } catch {
          return [p.slug, []];
        }
      })
    );
    setImagesBySlug(Object.fromEntries(entries));
  }, [authAxios]);

  useEffect(() => { if (user) load(); }, [user, load]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(''), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const upload = async (slug, file) => {
    setError('');
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await authAxios.post(`/admin/products/${slug}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImagesBySlug((prev) => ({
        ...prev,
        [slug]: [...(prev[slug] || []), data].sort((a, b) => (a.position || 0) - (b.position || 0)),
      }));
      setFlash(`Uploaded "${file.name}" to ${slug.replace(/-/g, ' ')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : `Failed to upload ${file.name}`);
    } finally {
      setUploadingSlug(null);
    }
  };

  const remove = async (slug, imgId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await authAxios.delete(`/admin/products/${slug}/images/${imgId}`);
      setImagesBySlug((prev) => ({
        ...prev,
        [slug]: (prev[slug] || []).filter((i) => i.id !== imgId),
      }));
      setFlash('Photo removed');
    } catch {
      setError('Could not delete this photo');
    }
  };

  const handleLogout = () => { logout(); navigate('/admin/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-white border-b border-pink-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="w-10 h-10 rounded-full object-contain" />
            <div className="leading-tight">
              <div className="font-script text-xs text-pink-brand -mb-0.5">Simply Original</div>
              <div className="font-display font-bold text-stone-900">Mr. Coconetto · Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-stone-500">{user.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full border-pink-200 text-pink-brand hover:bg-blush/60"
              data-testid="images-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <AdminTabs />
        </div>

        <div className="mb-6">
          <h1 className="font-display font-bold text-2xl text-stone-900">Product Gallery</h1>
          <p className="text-sm text-stone-500 mt-1">
            Upload real photos of your work for each product. Once uploaded, they replace the placeholder images on the public product pages.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 text-red-700 text-sm px-4 py-2 border border-red-200" data-testid="images-error">
            {error}
          </div>
        )}
        {flash && (
          <div className="mb-4 rounded-xl bg-emerald-50 text-emerald-700 text-sm px-4 py-2 border border-emerald-200 inline-flex items-center gap-2" data-testid="images-flash">
            <CheckCircle2 className="w-4 h-4" /> {flash}
          </div>
        )}

        <div className="space-y-5">
          {PRODUCTS.map((p) => (
            <ProductPanel
              key={p.slug}
              product={p}
              images={imagesBySlug[p.slug] || []}
              onUpload={upload}
              onDelete={remove}
              uploading={uploadingSlug === p.slug}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminProductImages;
